/**
 * useStudioTemplates.ts
 *
 * Hook for fetching a browse builder's gallery card list from `GET
 * /studio/templates` (schema-service). Purely a data hook — it does no
 * permission filtering of its own (`galleryDoesNotCallAdminHooks`, EAINT-873
 * Phase 4 Acceptance Tests): the server is the only fail-closed boundary,
 * this hook and its callers only ever render what it returns.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import type { StudioTemplateCard } from 'types/studio';
import { getStudioTemplates, type GetStudioTemplatesParams } from '../api/studioApi';

export interface UseStudioTemplatesReturn {
  templates: StudioTemplateCard[];
  degraded: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the gallery card list for the given template types.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param params - `types` (required) plus optional `status`/`q` filters
 */
export function useStudioTemplates(
  apiClients: ApiClients | null,
  params: GetStudioTemplatesParams
): UseStudioTemplatesReturn {
  const [templates, setTemplates] = useState<StudioTemplateCard[]>([]);
  const [degraded, setDegraded] = useState(false);
  // Starts `true` for the same reason as `useStudioDesk`: the fetch is kicked off by the effect
  // below, so a `false` initial value would render the empty state before the request even fires.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const { status, q } = params;
  const typesKey = params.types.join(',');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTemplates = useCallback(async () => {
    if (!apiClients?.schemaClient || !typesKey) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getStudioTemplates(apiClients.schemaClient, { types: typesKey.split(','), status, q });
      if (isMountedRef.current) {
        setTemplates(data.templates);
        setDegraded(data.degraded);
      }
    } catch (err: unknown) {
      if (isMountedRef.current) {
        // Fail closed: drop whatever was rendered rather than leaving a previous (possibly
        // previous-identity) list on screen next to an error.
        setTemplates([]);
        setDegraded(true);
        setError(err instanceof Error ? err.message : 'Failed to fetch Studio templates');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients, typesKey, status, q]);

  useEffect(() => {
    if (!apiClients || !typesKey) {
      setTemplates([]);
      setDegraded(false);
      setLoading(false);
      return;
    }

    setTemplates([]);
    fetchTemplates();
  }, [apiClients, typesKey, fetchTemplates]);

  return { templates, degraded, loading, error, refetch: fetchTemplates };
}
